import {WidgetData} from '../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../core/shared/widget/service/widget.factory';
import {WidgetDataParam} from '../../../core/shared/widget/widget-data-param';
import {FilterCriteria} from '../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../core/shared/misc/sorting';
import {QlQueryDto} from '../../../core/shared/ql/data/dto/ql-query.dto';
import {buildCwCuratedWidgetForm} from './cw-widget-form.util';

const WIDGET_ALIAS = 'config.cw.CwCaseTypeBipro.widget';

/**
 * Builds the "BiPRO-Detail" config table widget for cw.CwCaseTypeBipro (design G.5 node id
 * `bipro`) — the complete Partner x Norm x Version BiPRO/TAA coordinate of a CwCaseType
 * (M2-WP1 §1, exactly one row per CaseType whose processBinding is BIPRO_TAA). Read-mostly
 * inspection table: rows come from the alpha-cw-taa-bundle-generator SMART-Seed bundle, never
 * hand-added in the UI (wp4-fe-ruckfragen-resolved.md Q7 — the resulting canAdd:false /
 * canEdit:true posture is a node-view config concern, not this widget's).
 *
 * Mirrors CwConfigPage.createCaseTypeWidget(): WidgetFactory.createWidgetTableQl on an
 * explicit, hand-curated QlQueryDto (root cw.CwCaseTypeBipro aliased `e`, one LEFT join to the
 * owning caseType selecting only its display NAME) plus a curated WidgetForm for explicit
 * German column headers. The enum columns (biproNorm/flow/bundleMode/exchangeTraceLevel) are
 * selected as plain fields — the backend resolves their German label via @UiEnumValueInfo
 * metadata, so no FE enum mirror is needed for rendering (wp4-fe-spec.md §4.2). The FE mirror
 * CwExchangeTraceLevelEnum exists for the RUNTIME visibility logic on the case object's
 * "Fremdanbindung" tab, not for this table.
 *
 * @param caseTypeId optional cw.CwCaseType id to scope the table to one CaseType (e.g. embedded
 *   under that CaseType's node/detail); omit for the unscoped, all-CaseTypes inspection table.
 */
export function buildCwCaseTypeBiproTableWidget(caseTypeId?: number): WidgetData {
    const qlQuery: QlQueryDto = {
        name: WIDGET_ALIAS,
        type: 'view',
        start: {
            name: 'cw.CwCaseTypeBipro',
            as: 'e',
            type: 'entity',
            fields: [
                {name: 'id'},
                {name: 'biproServiceAlias'}, // BiPRO-Service-Alias
                {name: 'biproNorm'},         // BiPRO-Norm (Enum)
                {name: 'flow'},              // Ablauf/Flow (Enum)
                {name: 'biproVersion'},      // BiPRO-Version
                {name: 'bundleMode'},        // Bündel-Modus (Enum)
                // Trace configuration. These do NOT appear "automatically through the metadata": this
                // widget is a HAND-CURATED QlQueryDto with an explicit field list plus a curated
                // WidgetForm — what is not listed here is not rendered. exchangeTraceLevel REPLACES the
                // removed boolean logExchangePayloads; both fields sit in FIELD_GROUP_CONFIGURATION on
                // the backend entity.
                {name: 'exchangeTraceLevel'}, // Austausch-Protokollierung (Enum)
                {name: 'traceRetentionDays'}, // Aufbewahrung — DELIBERATELY inert, see the form below
                {name: 'active'},            // Aktiv
            ],
            joins: [
                {
                    type: 'entity', name: 'caseType', as: 'ct', joinType: 'left',
                    fields: [{name: 'id'}, {name: 'name'}], // Vorgangstyp (resolved NAME)
                },
            ],
        },
        fixedFilters: caseTypeId != null
            ? [FilterCriteria.create('e.caseTypeId', FilterCriteria.cOperatorEqual, caseTypeId)]
            : [],
        sortings: [new Sorting('e.biproServiceAlias', true)],
    };

    /**
     * AI:
     * Status: "in progress"
     * Type: Widget
     * SubType: Runtime
     * Reason: CwCaseTypeBipro config table — the Partner/Norm/Version BiPRO coordinate per
     *   CwCaseType, curated columns (Vorgangstyp, Service-Alias, Norm, Flow, Version,
     *   Bündel-Modus, Austausch-Protokollierung, Aufbewahrung, Aktiv).
     */
    const widgetData = WidgetFactory.createWidgetTableQl(
        WIDGET_ALIAS,
        'BiPRO-Detail',
        qlQuery,
        WidgetDataParam.create('widgetForm', buildCwCaseTypeBiproWidgetForm()),
    );
    widgetData.noDataText = 'Keine BiPRO-Koordinaten konfiguriert';
    return widgetData;
}

function buildCwCaseTypeBiproWidgetForm() {
    return buildCwCuratedWidgetForm([
        {field: 'ct.name', uiLabel: 'Vorgangstyp'},
        {field: 'e.biproServiceAlias', uiLabel: 'BiPRO-Service-Alias'},
        {field: 'e.biproNorm', uiLabel: 'BiPRO-Norm'},
        {field: 'e.flow', uiLabel: 'Ablauf (Flow)'},
        {field: 'e.biproVersion', uiLabel: 'BiPRO-Version'},
        {field: 'e.bundleMode', uiLabel: 'Bündel-Modus'},
        {field: 'e.exchangeTraceLevel', uiLabel: 'Austausch-Protokollierung'},
        // The inert-retention hint is carried IN THE HEADER, not in a tooltip. The backend field's
        // uiDescription says "derzeit ohne Wirkung — vorgesehen für einen späteren automatischen
        // Löschlauf", and the curated WidgetForm this table uses renders no description line — so an
        // operator typing "30" here would otherwise believe deletion happens (design E11, Auflage 1).
        // The header is the one place in this surface that is guaranteed visible.
        {field: 'e.traceRetentionDays', uiLabel: 'Aufbewahrung Protokoll (Tage — derzeit ohne Wirkung)'},
        {field: 'e.active', uiLabel: 'Aktiv'},
    ]);
}
